import React, {forwardRef, useContext, useImperativeHandle, useRef, useState} from "react";
import WarningPopup, {useWarningState} from "../../components/WarningPopup";
import PagedSearchTable, {PagedCall, PagedTableFunctions} from "../../components/PagedSearchTable";
import PendingSubmissionQuery from "../../controllers/PendingSubmissionQuery";
import {dateFormat} from "../../date";
import PaginationRequestSearch from "../../controllers/PaginationRequestSearch";
import SubmissionPrepare from "./SubmissionPrepare";
import Dialog from "../../components/Dialog";
import PendingSubmissionQueryLeaf from "../../controllers/PendingSubmissionQueryLeaf";
import {dataIdsKey} from "../../array";
import AppContext from "../../appContext";
import PermissionEnum from "../../controllers/PermissionEnum";

export interface PendingSubmissionsRef {
    refresh: () => void;
}

export const PendingSubmissionsLeaf = forwardRef<PendingSubmissionsRef, {
    call: PagedCall<PaginationRequestSearch, PendingSubmissionQueryLeaf>;
    prepare: (dataIds: number[]) => void;
    archive: (dataIds: number[]) => void;
    newSubmission: () => void;
}>((props, ref) => {
    const warningState = useWarningState<number[]>([]);
    const pagedTableRef = useRef<PagedTableFunctions<PendingSubmissionQueryLeaf>>();
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const context = useContext(AppContext);

    function mergeAndPrepare() {
        const items = pagedTableRef.current?.getData() ?? [];
        const selected = items.filter(item => selectedKeys.includes(dataIdsKey(item.dataIds)));
        const allDataIds = selected.flatMap(s => s.dataIds);
        props.prepare(allDataIds);
    }

    // Expose the `refresh` method using useImperativeHandle
    useImperativeHandle(ref, () => ({
        refresh: () => {
            setSelectedKeys([]);
            pagedTableRef.current?.refresh();
        },
    }));

    function warning(row: PendingSubmissionQueryLeaf) {
        warningState.show(`Are you sure you want to delete ${row.clientName}?`, row.dataIds);
    }

    return (
        <>
            <WarningPopup state={warningState} onYes={(data) => props.archive(data)}/>
            <button className="btn-sm btn-primary m-2" onClick={props.newSubmission}>
                New
            </button>
            {selectedKeys.length >= 2 && (
                <button className="btn-sm btn-primary m-2" onClick={mergeAndPrepare}>
                    Merge & Prepare ({selectedKeys.length})
                </button>
            )}
            <PagedSearchTable
                componentRef={pagedTableRef}
                call={props.call}
                keyExtractor={item => dataIdsKey(item.dataIds)}
                selection={{selectedKeys, onSelectionChange: setSelectedKeys}}
                columns={[
                    {
                        header: "Day",
                        row: (item) => dateFormat(item.date),
                    },
                    {
                        header: "Client",
                        row: (item) => item.clientName,
                    },
                    {
                        header: "Farm",
                        row: (item) => item.farmName,
                    },
                    {
                        header: "Samples",
                        row: (item) => item.samples,
                    },
                    {
                        header: "Crops",
                        row: (item) => item.crops.join(", "),
                    },
                    {
                        header: "Latest entry",
                        row: (item) => dateFormat(item.latest),
                    },
                    {
                        header: <div className="text-right">Actions</div>,
                        row: (item) => (
                            <div className="text-right">
                                <div className="btn-primary btn-sm" onClick={() => props.prepare(item.dataIds)}>
                                    prepare
                                </div>
                                {context.hasPermission(PermissionEnum.SubmissionsArchive) &&
                                    <div className="btn-error btn-sm" onClick={() => warning(item)}>
                                        x
                                    </div>
                                }
                            </div>
                        ),
                    },
                ]}
            />
        </>
    );
});